import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';

import { useIntl, FormattedMessage, FormattedNumber } from 'react-intl';
import { useRouteMatch } from 'react-router';
import Box from 'ui-box';
import { ProductSelect } from './ProductSelect';
import {
  useToast,
  PageContainer,
  Icon,
  majorScale,
  defaultFills,
  Heading,
  PageLoader,
  Container,
  Button,
  PageHeader,
  Menu,
  MenuItem,
  MenuDivider,
  Popover,
  useAlert,
  Section,
  Grid,
  KeyValue,
} from '../../npaper';
import { useStores } from '../../stores/Root';
import { IProductGroup } from '../../stores/products/ProductGroupStore';
import ProductGroupDetailsDialog, {
  ContactGroupTitle,
} from './ProductGroupDetailsDialog';
import { getSnapshot } from 'mobx-state-tree';
import { IProduct, Product } from '../../stores';
import { ProductThumbnail } from './ProductThumbnail';
import { FetchedTable } from '../../npaper/table/FetchedTable';

const ProductGroupPage = observer(() => {
  const intl = useIntl();
  const toaster = useToast();
  const match = useRouteMatch<{ id: string }>();
  const id = match.params.id;

  const { productGroupStore, productStore } = useStores();

  const [productGroup, setProductGroup] = useState<IProductGroup>();
  const [notFound, setNotFound] = useState(false);
  const [editItem, setEditItem] = useState(false);

  useEffect(() => {
    let isSub = true;
    setProductGroup(undefined);
    productGroupStore
      .getItem(Number(id))
      .then((group) => isSub && setProductGroup(group))
      .catch(() => setNotFound(true));
    return () => {
      isSub = false;
    };
  }, [productGroupStore, id]);

  const deleteAlert = useAlert({
    title: intl.formatMessage({
      defaultMessage: 'Deleting group',
      description: 'Delete product group alert title',
    }),
    message: intl.formatMessage({
      defaultMessage:
        'Are you sure you want to delete this product group? This cannot be undone.',
      description: 'Delete product group alert message',
    }),
    confirmButtonText: intl.formatMessage({
      defaultMessage: 'Delete',
      description: 'Delete product group alert confirm btn text',
    }),
    intent: 'danger',
    icon: 'warning-sign',
  });

  return !productGroup ? (
    <PageContainer>
      {notFound ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <Icon
            icon="error"
            size={40}
            marginBottom={majorScale(2)}
            color={defaultFills.danger[500]}
          />
          <Heading size={400}>
            <FormattedMessage defaultMessage="Could not find product group" />
          </Heading>
          <p className="muted font-mono">ID {id}</p>
        </Box>
      ) : (
        <PageLoader />
      )}
    </PageContainer>
  ) : (
    <PageContainer>
      <ProductGroupDetailsDialog
        isShown={editItem}
        productGroup={productGroup}
        onClose={(group) => {
          setEditItem(false);
        }}
      />

      <Container>
        <PageHeader
          title={productGroup.name}
          type={intl.formatMessage({ defaultMessage: 'Product group' })}
          typeIcon="box"
          id={productGroup.id}
          actions={
            <Popover
              placement="bottom-end"
              content={(close) => (
                <Menu>
                  <MenuItem
                    icon="edit"
                    text="Edit details"
                    onClick={() => {
                      close();
                      setEditItem(true);
                    }}
                  />
                  <MenuDivider />
                  <MenuItem
                    icon="trash"
                    intent="danger"
                    text="Delete group"
                    onClick={() => {
                      close();
                      deleteAlert.show(async () =>
                        productGroupStore
                          .deleteItem(productGroup)
                          .catch(toaster.showDefaultError)
                      );
                    }}
                  />
                </Menu>
              )}
            >
              <Button iconRight="chevron-down">
                <FormattedMessage defaultMessage="Actions" />
              </Button>
            </Popover>
          }
        />
        <Grid>
          <Section
            className="col-span-12 lg:col-span-4"
            title={intl.formatMessage({ defaultMessage: 'Details' })}
            actions={
              <Button
                minimal
                iconRight="edit"
                onClick={() => {
                  setEditItem(true);
                }}
              >
                <FormattedMessage defaultMessage="Edit details" />
              </Button>
            }
          >
            <KeyValue
              keyName={intl.formatMessage({ defaultMessage: 'Name' })}
              value={productGroup.name}
            />
            <KeyValue
              keyName={intl.formatMessage({ defaultMessage: 'Description' })}
              value={productGroup.description}
            />
            <KeyValue
              keyName={intl.formatMessage({
                defaultMessage: 'Default CMR',
                description: 'Label for default contribution margin ratio',
              })}
              value={
                <FormattedNumber
                  format="percent"
                  value={productGroup.default_cmr}
                />
              }
            />
            {productGroup.pricing.map((pricing) => (
              <KeyValue
                keyName={
                  pricing.contact_group && (
                    <ContactGroupTitle
                      contactGroupId={getSnapshot(pricing).contact_group}
                    />
                  )
                }
                value={<FormattedNumber format="percent" value={pricing.cmr} />}
              />
            ))}
          </Section>
          <Section
            className="col-span-12 lg:col-span-8"
            title={intl.formatMessage({ defaultMessage: 'Products' })}
            actions={
              <div>
                <ProductSelect
                  onSelect={(product) =>
                    // Add product to group (by updating the product's group)
                    productStore
                      .updateItem(product, {
                        group: productGroup.id,
                      })
                      .then(() => productGroupStore.invalidateCaches())
                      .catch(toaster.showDefaultError)
                  }
                  filter={{
                    group__neq: id,
                  }}
                >
                  <Button iconRight="cube-add" minimal>
                    <FormattedMessage defaultMessage="Add product" />
                  </Button>
                </ProductSelect>
              </div>
            }
          >
            <FetchedTable
              className="-mt-4"
              maxHeight="90vh"
              height="100%"
              header={[
                {
                  title: intl.formatMessage({ defaultMessage: 'Product name' }),
                },
                { title: '', width: 40 },
              ]}
              store={productStore as any}
              itemModel={Product}
              filter={{ filter: { group: productGroup.id } }}
              emptyPlaceholder={intl.formatMessage({
                defaultMessage: 'There are no products in this group',
              })}
              renderRow={(product: IProduct) => (
                <tr>
                  <td>
                    <ProductThumbnail product={product} />
                  </td>
                  <td style={{ width: 40 }}>
                    <Button
                      minimal
                      iconLeft="trash"
                      onClick={() =>
                        productStore
                          .updateItem(product, {
                            group: null,
                          })
                          .then(() => productStore.invalidateCaches())
                          .catch(toaster.showDefaultError)
                      }
                    />
                  </td>
                </tr>
              )}
            />
          </Section>
        </Grid>
      </Container>
    </PageContainer>
  );
});

export default ProductGroupPage;
